import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface AwardShortlistBook {
  title: string;
  author: string;
  position: number;
  isbn13?: string;
  isbn10?: string;
  bookId?: string;
  coverUrl?: string;
  matchConfidence?: 'high' | 'medium' | 'low';
}

export const useAwardShortlistSuggestion = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const suggestShortlist = async (prizeName: string, year: number): Promise<AwardShortlistBook[]> => {
    setLoading(true);
    setError(null);

    try {
      console.log(`🏆 Requesting shortlist for ${prizeName} ${year}`);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const { data, error: invokeError } = await supabase.functions.invoke('suggest-award-shortlist', {
        body: { prize_name: prizeName, year },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (invokeError) throw invokeError;
      
      // Handle case where AI doesn't have data
      if (data?.error === 'no_data' || !data?.shortlist || data.shortlist.length === 0) {
        throw new Error(
          data?.message || 
          `Couldn't find shortlist data for ${prizeName} ${year}. Please add books manually.`
        );
      }

      console.log(`📚 Received ${data.shortlist.length} books from AI`);

      // Enrich with our database
      const enriched = await enrichShortlistBooks(data.shortlist);
      console.log(`✨ Enriched ${enriched.length} books with database matches`);

      return enriched;
    } catch (err: any) {
      console.error('❌ Error suggesting shortlist:', err);
      setError(err.message || 'Failed to get shortlist suggestions');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { suggestShortlist, loading, error };
};

async function enrichShortlistBooks(books: AwardShortlistBook[]): Promise<AwardShortlistBook[]> {
  const enriched = await Promise.all(
    books.map(async (book) => {
      try {
        let dbBook = null;
        let matchConfidence: 'high' | 'medium' | 'low' = 'low';

        // Try ISBN-13 first (highest confidence)
        if (book.isbn13) {
          const { data } = await supabase
            .from('books')
            .select('id, title, authors, cover_url, cover_cache_url, isbn13')
            .eq('isbn13', book.isbn13)
            .single();
          
          if (data) {
            dbBook = data;
            matchConfidence = 'high';
            console.log(`✅ High confidence match for "${book.title}" via ISBN-13`);
          }
        }

        // Fallback to ISBN-10
        if (!dbBook && book.isbn10) {
          const { data } = await supabase
            .from('books')
            .select('id, title, authors, cover_url, cover_cache_url, isbn10')
            .eq('isbn10', book.isbn10)
            .single();
          
          if (data) {
            dbBook = data;
            matchConfidence = 'high';
            console.log(`✅ High confidence match for "${book.title}" via ISBN-10`);
          }
        }

        // Try title + author match
        if (!dbBook) {
          const { data: books } = await supabase
            .from('books')
            .select('id, title, authors, cover_url, cover_cache_url')
            .ilike('title', book.title)
            .limit(5);

          if (books && books.length > 0) {
            const authorLower = book.author.toLowerCase();
            dbBook = books.find(b => 
              b.authors?.some(a => a.toLowerCase().includes(authorLower))
            );
            
            if (dbBook) {
              matchConfidence = 'medium';
              console.log(`⚠️ Medium confidence match for "${book.title}" via title+author`);
            }
          }
        }

        if (!dbBook) {
          console.log(`❌ No match found for "${book.title}" by ${book.author}`);
        }

        return {
          ...book,
          bookId: dbBook?.id,
          coverUrl: dbBook ? (dbBook.cover_cache_url || dbBook.cover_url) : undefined,
          matchConfidence
        };
      } catch (error) {
        console.error(`Error enriching book "${book.title}":`, error);
        return { ...book, matchConfidence: 'low' as const };
      }
    })
  );

  return enriched;
}